import { Link } from "react-router-dom";
import {
  Truck,
  ShieldCheck,
  Package,
  Sparkles,
  Calendar,
  Layers,
  Boxes,
  Tag,
  PackageOpen,
  Calculator,
  AlertTriangle,
  ArrowRight,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison à l'adresse de votre choix ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Livraison à domicile
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Vos cartons livrés à l&apos;adresse de votre choix
              {" "}{city.aCity}
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Parce que les semaines précédant un départ sont rythmées
              par de nombreuses démarches administratives, nous
              simplifions vos préparatifs en prenant en charge le
              transport de votre matériel.
            </p>
          </div>

          <div className="lg:col-span-5 space-y-4">
            {[
              {
                title: "Une couverture locale complète",
                body: "Que vous habitiez dans le centre historique, le quartier des Fleurs ou les zones d'activités de Carrières-sous-Poissy, notre équipe logistique vous livre à plat directement chez vous ou à vos bureaux.",
                Icon: Truck,
              },
              {
                title: "Flexibilité et confort",
                body: "Vous déterminez le moment de la réception dans votre planning. Vous vous évitez ainsi la corvée de charger des paquets volumineux dans votre voiture et gagnez un temps précieux pour commencer à faire vos cartons.",
                Icon: Calendar,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="flex items-start gap-4 bg-white border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
              >
                <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <div>
                  <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                    {title}
                  </h3>
                  <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                    {body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Kits prêts à l'emploi ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Kits clés en main
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Des kits de déménagement prêts à l&apos;emploi
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Pour vous faire gagner du temps et vous éviter de vous
              retrouver à court de fournitures au pire moment, nous
              avons conçu des solutions clés en main adaptées à chaque
              type d&apos;habitation.
            </p>

            <div className="mt-6 space-y-4">
              {[
                {
                  title: "Packs sur-mesure",
                  body: "Nos kits (Studio, Appartement ou Maison) regroupent une sélection minutieuse de cartons standards, de cartons renforcés pour les livres, de rubans adhésifs robustes et de rouleaux de film bulle.",
                  Icon: PackageOpen,
                },
                {
                  title: "Zéro calcul mental",
                  body: "Plus besoin de deviner le nombre exact de boîtes nécessaires pour votre logement dans les Yvelines. Nos assortiments sont calculés par des professionnels pour correspondre parfaitement aux volumes habituels de chaque espace.",
                  Icon: Calculator,
                },
              ].map(({ title, body, Icon }) => (
                <div
                  key={title}
                  className="flex items-start gap-4 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
                >
                  <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                    <Icon className="h-5 w-5" strokeWidth={2.4} />
                  </span>
                  <div>
                    <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                      {title}
                    </h3>
                    <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                      {body}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <Link
              to="/kits-cartons"
              className="mt-7 inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
              data-testid="city-carrieres-sous-poissy-cta-kits"
            >
              <Boxes className="h-4 w-4" strokeWidth={2.6} />
              Découvrir tous nos kits
              <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </Link>
          </div>

          <div className="lg:col-span-5 bg-[#EEF3F7] border border-[#cfdbe6] rounded-3xl p-6">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-12 w-12 rounded-full bg-[#237cb5] text-white">
                <PackageOpen className="h-6 w-6" strokeWidth={2.4} />
              </span>
              <div className="font-display font-extrabold text-[20px] text-[#0f1d2b] leading-tight">
                Trois formats au choix
              </div>
            </div>
            <p className="mt-4 text-[14px] text-[#3a4856] leading-relaxed">
              Studio, Appartement ou Maison : le bon nombre de cartons,
              d&apos;adhésifs et de film à bulles dans une seule
              commande, livrée à domicile.
            </p>
            <div className="mt-5 space-y-2.5 text-[13px] text-[#3a4856]">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Kit Studio (T1)
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Kit Appartement (T2/T3)
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Kit Maison familiale
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Volumes calculés par des pros
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Erreurs à éviter ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Erreurs à éviter
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Les erreurs à éviter lors de l&apos;emballage
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Pour garantir que vos meubles et objets fragiles arrivent
            intacts à votre nouvelle adresse, voici les pièges fréquents
            à contourner pendant vos préparatifs.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {[
            {
              n: "Piège 1",
              title: "Surcharger les grands cartons",
              body: "Réservez les grands volumes aux objets légers comme les couettes ou les vêtements. Les éléments lourds (livres, vaisselle) doivent impérativement être placés dans de petits cartons renforcés pour éviter qu'ils ne cèdent sous le poids.",
              Icon: AlertTriangle,
            },
            {
              n: "Piège 2",
              title: "Laisser des espaces vides",
              body: "Un carton mal rempli risque de s'écraser sous le poids des autres dans le camion. Utilisez notre papier bulle ou du linge de maison pour caler le contenu et combler les vides.",
              Icon: Layers,
            },
            {
              n: "Piège 3",
              title: "Négliger l'étiquetage",
              body: "Attendre la fin du déménagement pour marquer vos boîtes est une source de stress. Notez la pièce de destination et la mention « Fragile » dès la fermeture de chaque carton.",
              Icon: Tag,
            },
          ].map(({ n, title, body, Icon }) => (
            <div
              key={n}
              className="flex items-start gap-4 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
            >
              <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Icon className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#237cb5]">
                  {n}
                </div>
                <h3 className="mt-1 font-display font-extrabold text-[16px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>

        <p className="mt-8 max-w-3xl text-[15px] md:text-base text-[#3a4856] leading-relaxed">
          En évitant ces quelques erreurs et en choisissant du matériel
          de qualité professionnelle, vous mettez toutes les chances de
          votre côté pour un déménagement sans accroc. Protéger vos
          biens dès l&apos;emballage, c&apos;est s&apos;assurer une
          installation sereine et efficace dans votre futur lieu de vie.
        </p>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/emballage-protection"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-carrieres-sous-poissy-cta-protection"
          >
            <ShieldCheck className="h-4 w-4" strokeWidth={2.6} />
            Voir nos emballages de protection
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-carrieres-sous-poissy-cta-shop"
          >
            <Boxes className="h-4 w-4" strokeWidth={2.6} />
            Voir tous les cartons
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-t border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-carrieres-sous-poissy-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
